/**
 * Payload building for the lead panel's one-click reach-out (PREC-3825): a lead that is on no list
 * has no `ListPerson`, so the Chat view cannot open. Instead of disabling the button we add the lead
 * to the company's hidden Compose list first — the same `POST v1/list/person` path the Global Search
 * "Write message" flow uses ({@link useComposeWriteMessage}).
 */

/** Request body accepted by the Compose-list add (`POST v1/list/person`). */
export type ComposeAddBody =
	| { method: "linkedinUrl"; linkedinUrl: string }
	| { method: "nameCompany"; name: string; companyName?: string };

/** What the lead panel knows about the lead — enough to identify them to the Compose add. */
export interface ComposeReachOutSource {
	linkedinUrl?: string | null;
	name?: string | null;
	companyName?: string | null;
}

/** Trimmed value, or `undefined` when the input is missing/blank. */
const cleaned = (value?: string | null): string | undefined => {
	const trimmed = value?.trim();

	return trimmed ? trimmed : undefined;
};

/**
 * Resolve a lead into the Compose-add payload. Prefers the LinkedIn URL — it is the reliable path
 * that matches the lead back onto its existing prospect — and falls back to name (+ company) only
 * when no URL is stored. Returns `undefined` when the lead carries neither, in which case the
 * reach-out button stays disabled with its "add to a list first" hint.
 */
export const buildComposeAddBody = ({
	linkedinUrl,
	name,
	companyName,
}: ComposeReachOutSource): ComposeAddBody | undefined => {
	const url = cleaned(linkedinUrl);

	if (url) {
		return { method: "linkedinUrl", linkedinUrl: url };
	}

	const personName = cleaned(name);

	if (!personName) {
		return undefined;
	}

	return { method: "nameCompany", name: personName, companyName: cleaned(companyName) };
};

/** Whether a list-less lead can be reached out to via the Compose list. */
export const canComposeReachOut = (source: ComposeReachOutSource): boolean =>
	!!buildComposeAddBody(source);
